import { useState, useEffect } from 'react';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { Select } from '@@/form-components/ReactSelect';

import {
  ByteUnit,
  byteUnitOptions,
  convertBytesToHumanReadable,
  formatDisplayValue,
} from '../../bytes';

interface ThresholdInputProps {
  name: string;
  value: number;
  onChange: (value: number) => void;
  onBlur?: () => void;
  error?: string;
  metricType: 'percentage' | 'bytes' | 'raw' | 'days';
  'data-cy'?: string;
}

export function ThresholdInput({
  name,
  value,
  onChange,
  onBlur,
  error,
  metricType,
  'data-cy': dataCy,
}: ThresholdInputProps) {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [selectedUnit, setSelectedUnit] = useState<ByteUnit>('MB');

  // Initialize display values based on the raw threshold value
  useEffect(() => {
    if (metricType === 'bytes') {
      if (value === 0) {
        setDisplayValue('0');
        return;
      }
      const { value: convertedValue, unit } =
        convertBytesToHumanReadable(value);
      setDisplayValue(convertedValue.toString());
      setSelectedUnit(unit);
      return;
    }
    setDisplayValue(value.toString());
  }, [value, metricType]);

  function handleDisplayValueChange(newDisplayValue: string) {
    setDisplayValue(newDisplayValue);

    const numericValue = parseFloat(newDisplayValue);
    if (Number.isNaN(numericValue)) {
      onChange(0);
      return;
    }

    if (metricType === 'bytes') {
      const unitConfig = byteUnitOptions.find((u) => u.value === selectedUnit);
      const bytesValue = Math.round(
        numericValue * (unitConfig?.multiplier || 1)
      );
      onChange(bytesValue);
    } else {
      onChange(numericValue);
    }
  }

  function handleUnitChange(unit: ByteUnit) {
    const numericValue = parseFloat(displayValue);
    if (Number.isNaN(numericValue)) {
      setSelectedUnit(unit);
      return;
    }

    const previousUnitConfig = byteUnitOptions.find(
      (u) => u.value === selectedUnit
    );
    const nextUnitConfig = byteUnitOptions.find((u) => u.value === unit);

    if (!previousUnitConfig || !nextUnitConfig) {
      setSelectedUnit(unit);
      return;
    }

    const bytesValue = Math.round(numericValue * previousUnitConfig.multiplier);
    const convertedDisplayValue = bytesValue / nextUnitConfig.multiplier;

    setSelectedUnit(unit);
    setDisplayValue(formatDisplayValue(convertedDisplayValue));

    onChange(bytesValue);
  }

  function getLabel() {
    switch (metricType) {
      case 'percentage':
        return 'Threshold (%)';
      case 'days':
        return 'Threshold (days)';
      case 'bytes':
      default:
        return 'Threshold';
    }
  }

  function getTooltip() {
    switch (metricType) {
      case 'percentage':
        return 'The percentage threshold for the condition (0-100)';
      case 'bytes':
        return `The threshold value for the condition. Raw value: ${value.toLocaleString()} bytes`;
      case 'days':
        return 'The number of days before expiry to trigger the alert';
      default:
        return 'The threshold value for the condition';
    }
  }

  function getPlaceholder() {
    switch (metricType) {
      case 'percentage':
        return 'Enter percentage (e.g., 80)';
      case 'bytes':
        return 'Enter value (e.g., 500)';
      case 'days':
        return 'Enter number of days (e.g., 7)';
      default:
        return 'Enter threshold';
    }
  }

  return (
    <FormControl
      label={getLabel()}
      inputId="threshold-input"
      errors={error}
      required
      tooltip={getTooltip()}
    >
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            id="threshold-input"
            name={name}
            type="number"
            value={displayValue}
            onChange={(e) => handleDisplayValueChange(e.target.value)}
            onBlur={onBlur}
            placeholder={getPlaceholder()}
            data-cy={dataCy || 'threshold-input'}
            min={metricType === 'percentage' ? 0 : undefined}
            max={metricType === 'percentage' ? 100 : undefined}
            step={metricType === 'bytes' ? 'any' : 1}
          />
        </div>

        {metricType === 'bytes' && (
          <div className="w-1/6">
            <Select
              id="threshold-unit-select"
              options={byteUnitOptions}
              value={byteUnitOptions.find(
                (unit) => unit.value === selectedUnit
              )}
              onChange={(option) =>
                option && handleUnitChange(option.value as ByteUnit)
              }
              isSearchable={false}
              data-cy="threshold-unit-select"
            />
          </div>
        )}

        {metricType === 'percentage' && (
          <div className="text-gray-600 flex items-center px-3">%</div>
        )}
        {metricType === 'days' && (
          <div className="text-gray-600 flex items-center px-3">days</div>
        )}
      </div>

      {metricType === 'bytes' && (
        <div className="text-gray-500 mt-1 text-xs">
          Raw value: {value.toLocaleString()} bytes
        </div>
      )}
    </FormControl>
  );
}
